import React from 'react';
import { FACEDOWN } from 'constants/gameConstants';
import { sortHoleCards } from 'utils/sortHoleCards';

import { isFaceDownCard, mapToCard } from 'components/Card';
import SmallCard from 'components/Card/SmallCard';

import styles from './HandHistory.module.scss';

const sameCard = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

/** True when `card` is one of `highlight` (card codes compare case-insensitively). */
export const isHighlightedCard = (card: string | undefined, highlight?: string[]) =>
  Boolean(card && highlight?.some((candidate) => sameCard(candidate, card)));

export const PlayingCard = ({
  card,
  revealed = true,
  size = 'base',
  dimmed = false
}: {
  card?: string;
  revealed?: boolean;
  size?: 'base' | 'small' | 'xsmall';
  /** The card is shown but did not take part in the winning hand. */
  dimmed?: boolean;
}) => {
  const isBack = !card || !revealed || isFaceDownCard(card);
  const pokerCard = isBack ? null : mapToCard(card);
  const sizeClass =
    size === 'small' ? styles.cardShellSmall : size === 'xsmall' ? styles.cardShellXSmall : '';

  return (
    <span
      className={`${styles.cardShell} ${sizeClass} ${dimmed ? styles.cardDimmed : ''}`}
      data-dimmed={dimmed || undefined}>
      <span className={styles.cardScaler}>
        <SmallCard {...(pokerCard ?? {})} isFaceDown={isBack || !pokerCard} />
      </span>
    </span>
  );
};

export const CardStrip = ({
  cards,
  revealed,
  emptyText = 'Pre-flop - no board',
  size = 'small',
  highlight,
  dimmed = false
}: {
  cards?: string[];
  revealed?: boolean;
  emptyText?: string;
  size?: 'base' | 'small' | 'xsmall';
  /**
   * The cards that made the winning hand. When given, every other face-up
   * card in the strip is dimmed so the five that played stand out.
   */
  highlight?: string[];
  /** Dim the whole strip: a runout the viewer lost on a hand they won elsewhere. */
  dimmed?: boolean;
}) => {
  if (!cards || cards.length === 0) {
    return <span className={styles.emptyCards}>{emptyText}</span>;
  }

  const dimOthers = Boolean(highlight && highlight.length > 0);

  return (
    // A span, not a div: the strip sits inside the hand card's header button,
    // which may only hold phrasing content.
    <span className={styles.cardStrip}>
      {cards.map((card, index) => {
        const isFaceDown = !revealed || isFaceDownCard(card);

        return (
          <PlayingCard
            key={`${card}-${index}`}
            card={isFaceDown ? FACEDOWN : card}
            revealed={!isFaceDown}
            size={size}
            dimmed={(dimmed || (dimOthers && !isHighlightedCard(card, highlight))) && !isFaceDown}
          />
        );
      })}
    </span>
  );
};

/** Hole-card displays follow the same order as the table, including equal-rank ties. */
export const HoleCards = ({ cards, ...props }: React.ComponentProps<typeof CardStrip>) => (
  <CardStrip {...props} cards={cards && sortHoleCards(cards)} />
);

export const BoardCards = ({
  cards,
  emptyText = 'Pre-flop — no board',
  size = 'small',
  highlight,
  dimmed
}: {
  cards: string[];
  emptyText?: string;
  size?: 'base' | 'small' | 'xsmall';
  highlight?: string[];
  dimmed?: boolean;
}) => (
  // Only what was dealt: a hand that ended on the turn shows four cards and no
  // placeholder for the river (Jason, 28 Sep).
  <CardStrip
    cards={cards.filter((card) => card && mapToCard(card))}
    emptyText={emptyText}
    revealed
    size={size}
    highlight={highlight}
    dimmed={dimmed}
  />
);
